import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowLeft, Check, ChevronRight, ExternalLink, Flag, Library, Loader2,
  Pencil, Search, Trash2, Upload, X,
} from "lucide-react";
import { api, ApiError, bibliotecaArquivoUrl } from "../lib/api";
import { usePermissoes } from "../lib/permissoes";

interface Props {
  condominioId: number;
  onVoltar: () => void;
}

interface Documento {
  id: number;
  titulo: string;
  resumo: string | null;
  categoria: string;
  palavras_chave: string | null;
  partes: string | null;
  numero: string | null;
  data_documento: string | null;
  data_validade: string | null;
  nome_arquivo: string;
  tamanho_bytes: number | null;
  criado_em: string | null;
  dias_para_vencer: number | null;
  vencido: boolean;
  tem_texto: boolean;
  trecho?: string | null;
  pagina_busca?: number | null;
}
interface BibliotecaResp {
  condominio_id: number;
  itens: Documento[];
  categorias: string[];
  contagem_por_categoria: Record<string, number>;
  total: number;
  total_vencidos: number;
  total_vencendo: number;
}

interface FormState {
  titulo: string; categoria: string; resumo: string; palavras_chave: string;
  partes: string; numero: string; data_documento: string; data_validade: string;
}
const VAZIO: FormState = {
  titulo: "", categoria: "Documentos do Condomínio", resumo: "", palavras_chave: "",
  partes: "", numero: "", data_documento: "", data_validade: "",
};

function fmtData(iso: string | null): string {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

/** Etiqueta colorida do vencimento. */
function ValidadeBadge({ doc }: { doc: Documento }) {
  if (!doc.data_validade) return null;
  const dias = doc.dias_para_vencer;
  let cls = "text-emerald-700 bg-emerald-50 border-emerald-200";
  let txt = `Válido até ${fmtData(doc.data_validade)}`;
  if (doc.vencido) {
    cls = "text-rose-700 bg-rose-50 border-rose-200";
    txt = `Vencido em ${fmtData(doc.data_validade)}`;
  } else if (dias != null && dias <= 60) {
    cls = "text-amber-700 bg-amber-50 border-amber-200";
    txt = `Vence em ${dias} dia${dias === 1 ? "" : "s"} (${fmtData(doc.data_validade)})`;
  }
  // Bandeirinha só aparece quando há atenção (vencido = vermelha, vencendo = amarela).
  const flag = doc.vencido
    ? <Flag size={12} className="text-rose-600 fill-rose-500 shrink-0" />
    : (dias != null && dias <= 60)
      ? <Flag size={12} className="text-amber-600 fill-amber-400 shrink-0" />
      : null;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium border rounded px-1.5 py-0.5 ${cls}`}>
      {flag ?? <AlertTriangle size={11} />} {txt}
    </span>
  );
}

/** Indicador compacto de validade na linha (só chama atenção se vencido/perto). */
function ValidadePill({ doc }: { doc: Documento }) {
  if (!doc.data_validade) return null;
  const dias = doc.dias_para_vencer;
  if (doc.vencido)
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-700 bg-rose-100 rounded px-1.5 py-0.5 shrink-0">
        <Flag size={11} className="text-rose-600 fill-rose-500" /> VENCIDO
      </span>
    );
  if (dias != null && dias <= 60)
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 shrink-0">
        <Flag size={11} className="text-amber-600 fill-amber-400" /> vence {dias}d
      </span>
    );
  return null;
}

export function BibliotecaPage({ condominioId, onVoltar }: Props) {
  const { podeEscrever } = usePermissoes();
  const podeEditar = podeEscrever(condominioId, "biblioteca");
  const qc = useQueryClient();
  const [buscaInput, setBuscaInput] = useState("");
  const [busca, setBusca] = useState("");        // debounced (busca no servidor)
  const [catFiltro, setCatFiltro] = useState<string | null>(null);
  const [modal, setModal] = useState<null | { editar: Documento | null }>(null);
  // Controle de expansão por documento. undefined = usa o padrão (aberto se deu match na busca).
  const [override, setOverride] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const t = setTimeout(() => setBusca(buscaInput), 300);
    return () => clearTimeout(t);
  }, [buscaInput]);

  // Ao mudar a busca, esquece as expansões manuais (volta ao padrão da nova busca).
  useEffect(() => { setOverride({}); }, [busca]);

  const { data, isLoading } = useQuery<BibliotecaResp>({
    queryKey: ["biblioteca", condominioId, busca, catFiltro],
    queryFn: () => {
      const p = new URLSearchParams();
      if (busca.trim()) p.set("busca", busca.trim());
      if (catFiltro) p.set("categoria", catFiltro);
      const qs = p.toString();
      return api.get<BibliotecaResp>(`/condominios/${condominioId}/biblioteca${qs ? "?" + qs : ""}`);
    },
  });

  const itens = data?.itens ?? [];
  const contagem = data?.contagem_por_categoria ?? {};
  const totalGeral = data?.total ?? 0;

  const invalidar = () => qc.invalidateQueries({ queryKey: ["biblioteca", condominioId] });
  const excluir = useMutation({
    mutationFn: (id: number) => api.delete(`/biblioteca/${id}`),
    onSuccess: invalidar,
  });
  const indexar = useMutation({
    mutationFn: (id: number) => api.post(`/biblioteca/${id}/indexar`),
    onSuccess: invalidar,
    onError: (e) => {
      const msg = e instanceof ApiError
        ? ((e.body as { detail?: string })?.detail ?? e.message)
        : "Falha ao tornar pesquisável.";
      window.alert(`Não consegui tornar este documento pesquisável.\n\nMotivo: ${msg}`);
    },
  });

  const abrir = (d: Documento, pagina?: number | null) =>
    window.open(
      bibliotecaArquivoUrl(d.id) + (pagina && pagina > 1 ? `#page=${pagina}` : ""),
      "_blank",
    );
  const buscando = busca.trim() !== "";

  return (
    <div>
      <button type="button" onClick={onVoltar}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft size={16} /> Voltar
      </button>

      <div className="flex items-center gap-2 mb-1">
        <Library size={22} className="text-brand-700" />
        <h2 className="text-2xl font-semibold text-slate-900">Biblioteca de Documentos</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5 max-w-3xl">
        O arquivo digital do condomínio: convenção, regimento, PPCI, alvará, laudos,
        contratos e mais. Cada documento tem título, resumo e palavras-chave para você
        achar rápido. Documentos com validade avisam quando estão perto de vencer.
      </p>

      {/* Avisos de vencimento — vermelho forte se há vencidos */}
      {data && (data.total_vencidos > 0 || data.total_vencendo > 0) && (
        <div className={`mb-4 rounded-xl border-l-4 px-4 py-3.5 text-sm flex items-center gap-3 shadow-sm ${
          data.total_vencidos > 0
            ? "border-rose-500 bg-rose-50 text-rose-900"
            : "border-amber-500 bg-amber-50 text-amber-900"}`}>
          <AlertTriangle size={22} className={`shrink-0 ${data.total_vencidos > 0 ? "text-rose-600" : "text-amber-600"}`} />
          <div className="flex-1">
            {data.total_vencidos > 0 && (
              <span className="font-bold">⚠ {data.total_vencidos} documento(s) VENCIDO(S)!</span>
            )}
            {data.total_vencidos > 0 && data.total_vencendo > 0 && <span> </span>}
            {data.total_vencendo > 0 && (
              <span className="font-semibold">{data.total_vencendo} vencendo nos próximos 60 dias.</span>
            )}
            <span className="block text-[12px] opacity-90 mt-0.5">
              Confira as etiquetas coloridas na lista e renove o quanto antes.
            </span>
          </div>
          <button type="button" onClick={() => { setCatFiltro(null); setBuscaInput(""); }}
            className="shrink-0 text-xs font-semibold underline hover:opacity-80">Ver na lista</button>
        </div>
      )}

      {/* Barra: busca + enviar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input value={buscaInput} onChange={(e) => setBuscaInput(e.target.value)}
            placeholder="Buscar por título, palavra-chave, fornecedor OU conteúdo do documento…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-md" />
        </div>
        {podeEditar && (
          <button type="button" onClick={() => setModal({ editar: null })}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-700 hover:bg-brand-800 text-white text-sm font-medium rounded-md">
            <Upload size={16} /> Enviar documento
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-400 -mt-1 mb-3">
        A busca também procura <b>dentro do texto</b> dos documentos pesquisáveis (🔍) — ex.: digite um nome que apareça no contrato.
      </p>

      {/* Índice por categoria (contagem do acervo completo) */}
      {data && data.categorias.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          <Chip label={`Todos (${totalGeral})`} ativo={catFiltro === null} onClick={() => setCatFiltro(null)} />
          {data.categorias.map((c) => {
            const n = contagem[c] ?? 0;
            if (n === 0 && catFiltro !== c) return null;
            return <Chip key={c} label={`${c} (${n})`} ativo={catFiltro === c} onClick={() => setCatFiltro(c)} />;
          })}
        </div>
      )}

      {/* Lista */}
      {isLoading ? (
        <div className="text-sm text-slate-400 py-8 text-center">Carregando…</div>
      ) : itens.length === 0 ? (
        <div className="text-sm text-slate-400 py-10 text-center border border-dashed border-slate-200 rounded-xl">
          {totalGeral === 0
            ? "Nenhum documento ainda. Clique em “Enviar documento” para começar."
            : "Nada encontrado com esse filtro/busca."}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl bg-white divide-y divide-slate-100 overflow-hidden">
          {itens.map((d) => {
            // Padrão: durante uma busca, abre os quadros que deram match. Ação manual manda.
            const padraoAberto = Boolean(buscando && d.trecho);
            const exp = override[d.id] ?? padraoAberto;
            const alternar = () => setOverride((o) => ({ ...o, [d.id]: !exp }));
            return (
              <div key={d.id}>
                {/* Linha compacta — só o título. Clique abre o quadro. */}
                <button type="button" onClick={alternar}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition">
                  <ChevronRight size={15} className={`text-slate-400 shrink-0 transition-transform ${exp ? "rotate-90" : ""}`} />
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium text-slate-800 truncate" title={d.titulo}>{d.titulo}</span>
                    {exp && d.trecho && (
                      <>
                        <span
                          role="link" tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); abrir(d, d.pagina_busca); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); abrir(d, d.pagina_busca); } }}
                          className="mt-0.5 flex items-center gap-1 text-[11px] text-brand-700 hover:underline cursor-pointer"
                          title="Abrir o PDF direto na página onde aparece">
                          <Search size={11} className="shrink-0" />
                          <span className="truncate">…{d.trecho}…</span>
                          {d.pagina_busca ? <span className="shrink-0 font-semibold">pág. {d.pagina_busca} ›</span> : <span className="shrink-0">abrir ›</span>}
                        </span>
                        <span className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-900 bg-amber-100 border border-amber-300 rounded-md px-2 py-1">
                          💡 Abriu o PDF? Aperte <kbd className="px-1 py-0.5 bg-white border border-amber-400 rounded text-[10px] font-bold">Ctrl</kbd>
                          + <kbd className="px-1 py-0.5 bg-white border border-amber-400 rounded text-[10px] font-bold">F</kbd>,
                          digite “{busca.trim()}” e pule às próximas ocorrências.
                        </span>
                      </>
                    )}
                  </span>
                  <ValidadePill doc={d} />
                  <span className="text-[10px] text-slate-400 shrink-0 hidden sm:inline">{d.categoria}</span>
                </button>

                {/* Quadro expandido — resumo + detalhes + ações */}
                {exp && (
                  <div className="px-4 pb-4 pt-1 bg-slate-50/70 border-t border-slate-100">
                    <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                      <span className="text-[10px] font-medium text-brand-700 bg-brand-50 border border-brand-100 rounded px-1.5 py-0.5">{d.categoria}</span>
                      <ValidadeBadge doc={d} />
                      <button type="button" onClick={() => setOverride((o) => ({ ...o, [d.id]: false }))}
                        className="ml-auto text-[11px] font-medium text-slate-500 hover:text-slate-800">
                        ▲ recolher
                      </button>
                    </div>
                    {/* Um retângulo pastel único: resumo + dados + palavras-chave */}
                    <div className="mb-3 rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2.5">
                      <span className="block text-[10px] font-semibold text-indigo-400 uppercase tracking-wide mb-1">Resumo</span>
                      {d.resumo
                        ? <p className="text-[13px] text-slate-700 whitespace-pre-line">{d.resumo}</p>
                        : <p className="text-[12px] text-slate-400 italic">Sem resumo.</p>}
                      {(d.numero || d.partes || d.data_documento) && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 mt-2 pt-2 border-t border-indigo-100">
                          {d.numero && <span><b className="text-slate-600">Nº:</b> {d.numero}</span>}
                          {d.partes && <span><b className="text-slate-600">Partes:</b> {d.partes}</span>}
                          {d.data_documento && <span><b className="text-slate-600">Documento:</b> {fmtData(d.data_documento)}</span>}
                        </div>
                      )}
                      {(d.palavras_chave || "").trim() && (
                        <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-indigo-100">
                          {(d.palavras_chave || "").split(",").map((p) => p.trim()).filter(Boolean).map((p, i) => (
                            <span key={i} className="text-[10px] text-slate-600 bg-white/80 border border-indigo-100 rounded px-1.5 py-0.5">{p}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button type="button" onClick={() => abrir(d, buscando ? d.pagina_busca : null)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-700 hover:bg-brand-800 text-white text-xs font-medium rounded-md">
                        <ExternalLink size={14} /> Abrir PDF
                      </button>
                      {podeEditar && (
                        <>
                          <button type="button" onClick={() => setModal({ editar: d })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:border-brand-400 text-slate-700 text-xs font-medium rounded-md">
                            <Pencil size={13} /> Editar
                          </button>
                          <button type="button"
                            onClick={() => { if (window.confirm(`Excluir "${d.titulo}"? (remove o arquivo)`)) { excluir.mutate(d.id); setOverride((o) => ({ ...o, [d.id]: false })); } }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-medium rounded-md">
                            <Trash2 size={13} /> Excluir
                          </button>
                        </>
                      )}
                      {d.tem_texto ? (
                        <span className="text-[11px] text-emerald-600 ml-auto" title="Busca por conteúdo ativada">🔍 pesquisável</span>
                      ) : podeEditar ? (
                        <button type="button" onClick={() => indexar.mutate(d.id)} disabled={indexar.isPending}
                          className="text-[11px] text-brand-700 hover:underline ml-auto disabled:opacity-50 inline-flex items-center gap-1"
                          title="Ler o conteúdo do PDF para permitir busca por texto (grátis)">
                          {indexar.isPending && indexar.variables === d.id
                            ? <><Loader2 size={11} className="animate-spin" /> Lendo…</>
                            : "Tornar pesquisável"}
                        </button>
                      ) : null}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-2 truncate" title={d.nome_arquivo}>📄 {d.nome_arquivo}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <DocumentoModal
          condominioId={condominioId}
          editar={modal.editar}
          categorias={data?.categorias ?? []}
          onFechar={() => setModal(null)}
          onSalvo={() => { qc.invalidateQueries({ queryKey: ["biblioteca", condominioId] }); setModal(null); }}
        />
      )}
    </div>
  );
}

function Chip({ label, ativo, onClick }: { label: string; ativo: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className={`text-xs px-2.5 py-1 rounded-full border transition ${
        ativo ? "bg-brand-700 text-white border-brand-700"
              : "bg-white text-slate-600 border-slate-300 hover:border-brand-400"}`}>
      {label}
    </button>
  );
}

// =====================================================================
// Modal de envio / edição
// =====================================================================
function DocumentoModal({
  condominioId, editar, categorias, onFechar, onSalvo,
}: {
  condominioId: number;
  editar: Documento | null;
  categorias: string[];
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [novaCat, setNovaCat] = useState(false);
  const [form, setForm] = useState<FormState>(() =>
    editar
      ? {
          titulo: editar.titulo, categoria: editar.categoria, resumo: editar.resumo ?? "",
          palavras_chave: editar.palavras_chave ?? "", partes: editar.partes ?? "",
          numero: editar.numero ?? "", data_documento: editar.data_documento ?? "",
          data_validade: editar.data_validade ?? "",
        }
      : VAZIO,
  );
  const set = (k: keyof FormState, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const opcoesCat = form.categoria && !categorias.includes(form.categoria)
    ? [...categorias, form.categoria] : categorias;

  const salvar = useMutation({
    mutationFn: async () => {
      if (editar) {
        return api.patch(`/biblioteca/${editar.id}`, { ...form });
      }
      const fd = new FormData();
      fd.append("arquivo", arquivo as File);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      return api.postForm(`/condominios/${condominioId}/biblioteca`, fd);
    },
    onSuccess: onSalvo,
  });

  const erro =
    salvar.error instanceof ApiError
      ? ((salvar.error.body as { detail?: string })?.detail ?? salvar.error.message)
      : salvar.error ? "Não foi possível salvar." : null;

  const podeSalvar = form.titulo.trim() !== "" && (editar !== null || arquivo !== null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full p-6 my-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Library size={18} className="text-brand-700" />
            {editar ? "Editar documento" : "Enviar documento"}
          </h3>
          <button type="button" onClick={onFechar} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        {!editar && (
          <div className="mb-3">
            <span className="block text-[11px] font-medium text-slate-500 mb-1">Arquivo PDF *</span>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf"
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
              className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5" />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Campo label="Título *" value={form.titulo} onChange={(v) => set("titulo", v)}
            placeholder="Ex.: Convenção do Condomínio" />
          <label className="block">
            <span className="block text-[11px] font-medium text-slate-500 mb-1">Categoria</span>
            {novaCat ? (
              <div className="flex gap-1">
                <input autoFocus value={form.categoria} onChange={(e) => set("categoria", e.target.value)}
                  placeholder="Nome da nova categoria"
                  className="flex-1 px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
                <button type="button" title="Voltar à lista"
                  onClick={() => { setNovaCat(false); set("categoria", categorias[0] ?? "Outros"); }}
                  className="px-2 text-xs text-slate-500 hover:text-slate-800 border border-slate-300 rounded-md">↩</button>
              </div>
            ) : (
              <select value={form.categoria}
                onChange={(e) => {
                  if (e.target.value === "__nova__") { setNovaCat(true); set("categoria", ""); }
                  else set("categoria", e.target.value);
                }}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
                {opcoesCat.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="__nova__">➕ Nova categoria…</option>
              </select>
            )}
          </label>
          <label className="block sm:col-span-2">
            <span className="block text-[11px] font-medium text-slate-500 mb-1">Resumo (do que trata o documento)</span>
            <textarea value={form.resumo} onChange={(e) => set("resumo", e.target.value)} rows={2}
              placeholder="Ex.: Convenção registrada em cartório em 2019, regras gerais do condomínio."
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          </label>
          <Campo label="Palavras-chave (separadas por vírgula)" value={form.palavras_chave}
            onChange={(v) => set("palavras_chave", v)} placeholder="convenção, cartório, 2019" />
          <Campo label="Partes / Fornecedor" value={form.partes}
            onChange={(v) => set("partes", v)} placeholder="Ex.: Empresa de Limpeza XYZ" />
          <Campo label="Número / código" value={form.numero}
            onChange={(v) => set("numero", v)} placeholder="Ex.: Contrato 123/2025" />
          <label className="block">
            <span className="block text-[11px] font-medium text-slate-500 mb-1">Data do documento</span>
            <input type="date" value={form.data_documento} onChange={(e) => set("data_documento", e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          </label>
          <label className="block">
            <span className="block text-[11px] font-medium text-slate-500 mb-1">Data de validade (vencimento)</span>
            <input type="date" value={form.data_validade} onChange={(e) => set("data_validade", e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          </label>
        </div>

        {erro && <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded text-xs text-red-800">{erro}</div>}

        <div className="mt-4 flex gap-2 justify-end">
          <button type="button" onClick={onFechar} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancelar</button>
          <button type="button" onClick={() => salvar.mutate()} disabled={!podeSalvar || salvar.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-sm font-semibold rounded-md">
            {salvar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            {editar ? "Salvar" : "Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Campo({
  label, value, onChange, placeholder,
}: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-slate-500 mb-1">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
    </label>
  );
}
